import { buildArtisnHeaders } from "@multicines/services";
import { useShoppingCartStore } from "@multicines/stores";
import { redeemCoupon } from "artisn/shopping-cart";
import { Badge } from "artisn-ui-react";
import Wallet from "images/wallet.svg";
import React, { useState, useMemo } from "react";

import Styles from "./RedeemCoupon.styles";
import { RedeemCouponProps as Props } from "./RedeemCoupon.types";
import RedeemCouponInput from "./RedeemCouponInput/RedeemCouponInput";
import CouponsModal from "components/coupons/CouponsModal/CouponsModal";
import Button from "components/global/Button/Button";
import ProductModal from "components/global/ProductModal/ProductModal";
import { notify } from "config/bugsnag.config";
import CONSTANTS from "config/constants";
import { useAuthStore } from "stores/auth/auth.store";
import { getBenefitProductId } from "utils/common.utils";
import { createSuccessNotification } from "utils/notifications.utils";

const { SHOPPING_CART_DEFAULT_NAME, ACCOUNT_ID } = CONSTANTS.ARTISN;
const { API_URL } = CONSTANTS.API;

const RedeemCoupon: React.FC<Props> = props => {
  const { className } = props;
  const isAnonymous = useAuthStore(state => state.isAnonymous);
  const uid = useAuthStore(state => state.uid);
  const [opened, setOpened] = useState(false);
  const [redeemCouponError, setRedeemCouponError] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const benefitsWallet = useShoppingCartStore(store => store.benefitsWallet);
  const temporalBenefit = useShoppingCartStore(store => store.temporalBenefit);
  const { benefits } = benefitsWallet ?? {};
  const benefitsLength = useMemo(() => benefits?.length, [benefits]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const productId = getBenefitProductId(temporalBenefit);

  const redeemHandler = async (code: string) => {
    if (code.trim() === "") return;

    try {
      setRedeemCouponError("");
      await redeemCoupon(
        {
          shoppingCartName: SHOPPING_CART_DEFAULT_NAME,
          apiURL: API_URL,
          anonymous: isAnonymous!,
          accountId: ACCOUNT_ID,
          customerId: uid!
        },
        code,
        await buildArtisnHeaders()
      );
      setCouponCode("");
      createSuccessNotification("Cupón redimido con éxito");
    } catch (e) {
      notify(e, "Redeem Coupon - Redeem Coupon");
      setRedeemCouponError(e.message);
    }
  };

  const openHandler = () => {
    setRedeemCouponError("");
    setOpened(prev => !prev);
  };

  const onClickModal = () => {
    setIsOpenModal(true);
  };

  const buttonNode = (
    <Button
      className="RedeemCoupon__button-wallet"
      onClick={() => openHandler()}
    >
      <Wallet />
    </Button>
  );

  return (
    <Styles className={`RedeemCoupon ${className}`}>
      <div className="RedeemCoupon__container">
        <RedeemCouponInput
          className="RedeemCoupon__input"
          disabled={!benefits}
          couponCode={couponCode}
          setCouponCode={setCouponCode}
          onRedeem={redeemHandler}
        />
        {benefitsLength ? (
          <Badge
            value={benefitsLength.toString()}
            className="RedeemCoupon__badge"
          >
            {buttonNode}
          </Badge>
        ) : (
          <div className="RedeemCoupon__badge">{buttonNode}</div>
        )}
      </div>
      {redeemCouponError ? (
        <p className="RedeemCoupon__redeem-error">{redeemCouponError}</p>
      ) : null}
      <CouponsModal
        opened={opened}
        benefits={benefits}
        redeemCouponError={redeemCouponError}
        redeemHandler={redeemHandler}
        openProductModal={onClickModal}
        setRedeemCouponError={setRedeemCouponError}
        onClose={() => setOpened(prev => !prev)}
      />
      <ProductModal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        productId={productId}
      />
    </Styles>
  );
};

export default RedeemCoupon;
